import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 红桃视频"
          description="您访问的文章不存在或已被删除。"
          keywords="红桃视频"
          canonicalUrl={`https://hongtaovideo.com/article/${id}`}
        />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = (article.relatedArticles || [])
    .map((rid: number) => articles.find((a) => a.id === rid))
    .filter(Boolean);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '红桃视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hongtaovideo.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 红桃视频资讯`}
        description={article.excerpt}
        keywords={`红桃视频,${article.tags.join(',')},短视频资讯`}
        canonicalUrl={`https://hongtaovideo.com/article/${article.id}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '资讯动态', to: '/article/1' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {article.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount} 阅读
                </span>
              </div>
            </div>

            <div className="relative aspect-video rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src={article.coverImageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
                [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-2"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedArticles.map((ra) => ra && (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="flex gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={ra.coverThumbUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-1">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500">{ra.publishDate} · {ra.readCount} 阅读</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}